import { useEffect, useMemo, useState } from 'react';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Icon from '@mui/material/Icon';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Link, Navigate, useLocation } from 'react-router-dom';

import {
  fetchProviderAnalytics,
  readCachedProviderAnalytics,
  type ProviderAnalyticsDashboard,
} from 'analytics';
import SoftBox from 'components/SoftBox';
import SoftButton from 'components/SoftButton';
import { showSuiErrorSnackbar } from 'components/SuiSnackbar';
import SoftTypography from 'components/SoftTypography';
import MiniStatisticsCard from 'examples/Cards/StatisticsCards/MiniStatisticsCard';
import DefaultLineChart from 'examples/Charts/LineCharts/DefaultLineChart';
import Footer from 'examples/Footer';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import { useAuth } from 'auth';
import { useLocale } from 'lib/i18n/client';
import { getTranslations } from 'lib/i18n/dictionary';
import { withLocale } from 'lib/i18n/path';
import {
  fetchProfileByUuid,
  readCachedProfileByUuid,
  type ProfileDetails,
} from 'profile';

type AnalyticsRange = 7 | 30 | 90;

const analyticsTableHeadSx = {
  display: 'table-header-group',
  p: 0,
  borderRadius: 0,
};

const analyticsTableSx = {
  minWidth: 0,
  '& th:first-of-type, & td:first-of-type': {
    pl: 0,
  },
  '& th:last-of-type, & td:last-of-type': {
    pr: 0,
  },
};

function formatNumber(value: number): string {
  return new Intl.NumberFormat().format(value);
}

function buildTrendChartData(analytics: ProviderAnalyticsDashboard | null) {
  if (!analytics) {
    return {
      labels: [],
      datasets: [],
    };
  }

  return {
    labels: analytics.daily.map((entry) => entry.date.slice(5)),
    datasets: [
      {
        label: 'Views',
        color: 'info',
        data: analytics.daily.map((entry) => entry.profileViews + entry.serviceViews),
      },
      {
        label: 'Matched clicks',
        color: 'dark',
        data: analytics.daily.map(
          (entry) => entry.matchedProfileClicks + entry.matchedServiceClicks
        ),
      },
    ],
  };
}

function ProfileViewsCard({
  title,
  count,
  isIncomplete,
  incompleteLabel,
  incompleteLinkTo,
  iconComponent,
}: {
  title: string;
  count: string;
  isIncomplete: boolean;
  incompleteLabel: string;
  incompleteLinkTo: string;
  iconComponent: string;
}) {
  if (!isIncomplete) {
    return (
      <MiniStatisticsCard
        title={{ text: title, fontWeight: 'medium' }}
        count={count}
        icon={{ color: 'primary', component: iconComponent }}
      />
    );
  }

  return (
    <Card>
      <SoftBox p={2}>
        <Grid container alignItems="center">
          <Grid item xs={8}>
            <SoftBox lineHeight={1.1}>
              <SoftTypography
                variant="button"
                color="text"
                textTransform="capitalize"
                fontWeight="medium"
              >
                {title}
              </SoftTypography>
              <SoftTypography
                component={Link}
                to={incompleteLinkTo}
                variant="button"
                color="error"
                fontWeight="medium"
                display="block"
                sx={{ textDecoration: 'none' }}
              >
                {incompleteLabel}
              </SoftTypography>
            </SoftBox>
          </Grid>
          <Grid item xs={4}>
            <SoftBox
              component={Link}
              to={incompleteLinkTo}
              variant="gradient"
              bgColor="error"
              color="white"
              width="3rem"
              height="3rem"
              marginLeft="auto"
              borderRadius="md"
              display="flex"
              justifyContent="center"
              alignItems="center"
              shadow="md"
              sx={{ textDecoration: 'none' }}
            >
              <Icon fontSize="small" color="inherit">
                {iconComponent}
              </Icon>
            </SoftBox>
          </Grid>
        </Grid>
      </SoftBox>
    </Card>
  );
}

function Analytics() {
  const location = useLocation();
  const { accessToken, activeProfile, activeProfileUuid, isProfilesLoading } = useAuth();
  const locale = useLocale();
  const translations = getTranslations(locale);
  const t = translations.analyticsPage;
  const [rangeDays, setRangeDays] = useState<AnalyticsRange>(30);
  const cachedAnalytics = activeProfileUuid
    ? readCachedProviderAnalytics(activeProfileUuid, 30)
    : undefined;
  const cachedActiveProfileDetails = activeProfileUuid
    ? readCachedProfileByUuid(activeProfileUuid)
    : undefined;
  const [analytics, setAnalytics] = useState<ProviderAnalyticsDashboard | null>(
    () => cachedAnalytics ?? null
  );
  const [activeProfileDetails, setActiveProfileDetails] = useState<
    ProfileDetails | null | undefined
  >(
    () => cachedActiveProfileDetails
  );
  const [isProfileDetailsLoading, setIsProfileDetailsLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(() => cachedAnalytics === undefined);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (errorMessage) {
      showSuiErrorSnackbar(errorMessage);
    }
  }, [errorMessage]);

  useEffect(() => {
    if (isProfilesLoading) {
      setIsLoading(true);
      return;
    }

    if (!activeProfileUuid) {
      setAnalytics(null);
      setIsLoading(false);
      setErrorMessage('');
      return;
    }

    let isCancelled = false;

    async function loadAnalytics() {
      const cachedData = readCachedProviderAnalytics(activeProfileUuid, rangeDays);

      if (!isCancelled && cachedData !== undefined) {
        setAnalytics(cachedData);
        setIsLoading(false);
      } else if (!isCancelled) {
        setIsLoading(true);
      }

      setErrorMessage('');

      try {
        const nextAnalytics = await fetchProviderAnalytics(
          activeProfileUuid,
          rangeDays,
          accessToken ?? undefined
        );

        if (!isCancelled) {
          setAnalytics(nextAnalytics);
        }
      } catch (error) {
        if (!isCancelled) {
          setAnalytics(null);
          setErrorMessage(
            error instanceof Error ? error.message : t.loadFailed
          );
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    }

    void loadAnalytics();

    return () => {
      isCancelled = true;
    };
  }, [accessToken, activeProfileUuid, isProfilesLoading, rangeDays, t.loadFailed]);

  useEffect(() => {
    if (!activeProfileUuid) {
      setActiveProfileDetails(null);
      setIsProfileDetailsLoading(false);
      return;
    }

    const cachedProfile = readCachedProfileByUuid(activeProfileUuid);
    setActiveProfileDetails(cachedProfile);

    if (cachedProfile !== undefined) {
      setIsProfileDetailsLoading(false);
      return;
    }

    let isCancelled = false;
    setIsProfileDetailsLoading(true);

    async function loadActiveProfileDetails() {
      try {
        const nextProfileDetails = await fetchProfileByUuid(
          activeProfileUuid,
          accessToken ?? undefined
        );

        if (!isCancelled) {
          setActiveProfileDetails(nextProfileDetails);
        }
      } catch {
        if (!isCancelled) {
          setActiveProfileDetails(undefined);
        }
      } finally {
        if (!isCancelled) {
          setIsProfileDetailsLoading(false);
        }
      }
    }

    void loadActiveProfileDetails();

    return () => {
      isCancelled = true;
    };
  }, [accessToken, activeProfileUuid]);

  const chartData = useMemo(
    () => buildTrendChartData(analytics) as unknown as { [key: string]: any[] },
    [analytics]
  );
  const profileViewsCount = analytics?.summary.profileViews ?? 0;
  const serviceViewsCount = analytics?.summary.serviceViews ?? 0;
  const contactClicksCount = analytics?.summary.contactClicks ?? 0;
  const socialClicksCount = analytics?.summary.socialClicks ?? 0;
  const hasResolvedProfileDetails = activeProfileDetails !== undefined;
  const isActiveProfileCompleted =
    (activeProfile?.categories?.length ?? 0) > 0 || (activeProfileDetails?.skills.length ?? 0) > 0;
  const hasContacts = (activeProfile?.contacts?.length ?? 0) > 0;
  const hasSocialLinks = (activeProfile?.socialLinks?.length ?? 0) > 0;
  const hasServices = (activeProfileDetails?.services.length ?? 0) > 0;
  const shouldShowProfileIncompleteState =
    ((activeProfile?.categories?.length ?? 0) > 0 || hasResolvedProfileDetails) &&
    !isProfileDetailsLoading &&
    !isActiveProfileCompleted &&
    profileViewsCount === 0;
  const shouldShowContactsIncompleteState =
    Boolean(activeProfileUuid) && !hasContacts && contactClicksCount === 0;
  const shouldShowSocialLinksIncompleteState =
    Boolean(activeProfileUuid) && !hasSocialLinks && socialClicksCount === 0;
  const shouldShowServicesIncompleteState =
    hasResolvedProfileDetails &&
    !isProfileDetailsLoading &&
    !hasServices &&
    serviceViewsCount === 0;
  const activeProfileRoute = activeProfileUuid
    ? withLocale(`/profile/${activeProfileUuid}`, locale)
    : withLocale('/pages/account/settings', locale);
  const servicesRoute = withLocale('/services', locale);
  const hasData =
    Boolean(analytics) &&
    (analytics.summary.profileViews > 0 ||
      analytics.summary.serviceViews > 0 ||
      analytics.summary.contactClicks > 0 ||
      analytics.summary.socialClicks > 0);

  if (
    location.pathname === '/applications/analytics' ||
    location.pathname === '/dashboards/default'
  ) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox pb={3}>
        <SoftBox mb={3}>
          <Card>
            <SoftBox p={3}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
                spacing={2}
              >
                <SoftBox lineHeight={1}>
                  <SoftTypography variant="h5" fontWeight="medium">
                    {t.title}
                  </SoftTypography>
                  <SoftTypography variant="button" fontWeight="regular" color="text">
                    {t.description}
                  </SoftTypography>
                </SoftBox>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <SoftButton
                    variant={rangeDays === 7 ? 'gradient' : 'outlined'}
                    color={rangeDays === 7 ? 'info' : 'secondary'}
                    onClick={() => setRangeDays(7)}
                  >
                    {t.range7d}
                  </SoftButton>
                  <SoftButton
                    variant={rangeDays === 30 ? 'gradient' : 'outlined'}
                    color={rangeDays === 30 ? 'info' : 'secondary'}
                    onClick={() => setRangeDays(30)}
                  >
                    {t.range30d}
                  </SoftButton>
                  <SoftButton
                    variant={rangeDays === 90 ? 'gradient' : 'outlined'}
                    color={rangeDays === 90 ? 'info' : 'secondary'}
                    onClick={() => setRangeDays(90)}
                  >
                    {t.range90d}
                  </SoftButton>
                </Stack>
              </Stack>
            </SoftBox>
          </Card>
        </SoftBox>

        {!activeProfileUuid && !isProfilesLoading ? (
          <Card>
            <SoftBox p={3}>
              <SoftTypography variant="button" color="text">
                {t.profileMissing}
              </SoftTypography>
            </SoftBox>
          </Card>
        ) : null}

        {isLoading ? (
          <Card>
            <SoftBox p={3}>
              <SoftTypography variant="button" color="text">
                {t.loading}
              </SoftTypography>
            </SoftBox>
          </Card>
        ) : null}

        {!isLoading && activeProfileUuid ? (
          <>
            <SoftBox mb={3}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} lg={3}>
                  <MiniStatisticsCard
                    title={{ text: t.uniqueVisitors, fontWeight: 'medium' }}
                    count={formatNumber(analytics?.summary.uniqueVisitors ?? 0)}
                    icon={{ color: 'primary', component: 'public' }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                  <MiniStatisticsCard
                    title={{ text: t.sessions, fontWeight: 'medium' }}
                    count={formatNumber(analytics?.summary.sessions ?? 0)}
                    icon={{ color: 'primary', component: 'watch' }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                  <ProfileViewsCard
                    title={t.profileViews}
                    count={formatNumber(profileViewsCount)}
                    isIncomplete={shouldShowProfileIncompleteState}
                    incompleteLabel={t.profileIncomplete}
                    incompleteLinkTo={activeProfileRoute}
                    iconComponent="account_circle"
                  />
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                  <ProfileViewsCard
                    title={t.serviceViews}
                    count={formatNumber(serviceViewsCount)}
                    isIncomplete={shouldShowServicesIncompleteState}
                    incompleteLabel={t.servicesIncomplete}
                    incompleteLinkTo={servicesRoute}
                    iconComponent="storefront"
                  />
                </Grid>
              </Grid>
            </SoftBox>

            <SoftBox mb={3}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} lg={3}>
                  <ProfileViewsCard
                    title={t.contactClicks}
                    count={formatNumber(contactClicksCount)}
                    isIncomplete={shouldShowContactsIncompleteState}
                    incompleteLabel={t.contactsIncomplete}
                    incompleteLinkTo={activeProfileRoute}
                    iconComponent="call"
                  />
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                  <ProfileViewsCard
                    title={t.socialClicks}
                    count={formatNumber(socialClicksCount)}
                    isIncomplete={shouldShowSocialLinksIncompleteState}
                    incompleteLabel={t.socialLinksIncomplete}
                    incompleteLinkTo={activeProfileRoute}
                    iconComponent="share"
                  />
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                  <MiniStatisticsCard
                    title={{ text: t.matchedProfileClicks, fontWeight: 'medium' }}
                    count={formatNumber(analytics?.summary.matchedProfileClicks ?? 0)}
                    icon={{ color: 'primary', component: 'ads_click' }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                  <MiniStatisticsCard
                    title={{ text: t.matchedServiceClicks, fontWeight: 'medium' }}
                    count={formatNumber(analytics?.summary.matchedServiceClicks ?? 0)}
                    icon={{ color: 'primary', component: 'touch_app' }}
                  />
                </Grid>
              </Grid>
            </SoftBox>

              <Grid container spacing={3}>
                <Grid item xs={12} lg={7}>
                  <DefaultLineChart
                    title={t.totalViewsChartTitle}
                    description={t.totalViewsChartDescription}
                    chart={chartData}
                  />
                </Grid>
                <Grid item xs={12} lg={5}>
                  <Card sx={{ height: '100%' }}>
                    <SoftBox p={3}>
                      <SoftTypography variant="h6" mb={2}>
                        {t.topCitiesTitle}
                      </SoftTypography>
                      {analytics?.topCities.length ? (
                        <SoftBox sx={{ overflowX: 'auto' }}>
                          <Table sx={analyticsTableSx}>
                            <TableHead sx={analyticsTableHeadSx}>
                              <TableRow>
                                <TableCell sx={{ width: '40%' }}>{t.cityColumn}</TableCell>
                                <TableCell align="right" sx={{ width: '20%' }}>
                                  {t.viewsColumn}
                                </TableCell>
                                <TableCell align="right" sx={{ width: '20%' }}>
                                  {t.visitorsColumn}
                                </TableCell>
                                <TableCell align="right" sx={{ width: '20%' }}>
                                  {t.sessionsColumn}
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {analytics.topCities.map((city) => (
                                <TableRow key={city.city}>
                                  <TableCell>{city.city}</TableCell>
                                  <TableCell align="right">
                                    {formatNumber(city.viewCount)}
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatNumber(city.uniqueVisitors)}
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatNumber(city.sessions)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </SoftBox>
                      ) : (
                        <SoftTypography variant="button" color="text">
                          {t.noData}
                        </SoftTypography>
                      )}
                    </SoftBox>
                  </Card>
                </Grid>
              </Grid>

            {!hasData ? (
              <SoftBox mt={3}>
                <Card>
                  <SoftBox p={3}>
                    <SoftTypography variant="button" color="text">
                      {t.noData}
                    </SoftTypography>
                  </SoftBox>
                </Card>
              </SoftBox>
            ) : null}
          </>
        ) : null}
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Analytics;
